import React from "react";
import { Box, Container, Typography, Divider, Link } from "@mui/material";

const TermsAndConditions = () => {
  return (
    <Container maxWidth="md">
      <Box sx={{ py: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
          Terms and Conditions
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Effective Date: June 2, 2026
        </Typography>
        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" paragraph>
          These Terms and Conditions ("Terms") govern your access to and use of
          the RoomRoster property management platform operated by Gleason
          Development LLC ("we," "us," or "our"). By accessing or using
          RoomRoster, you agree to be bound by these Terms. If you do not
          agree, do not use the service.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          1. Eligibility and Accounts
        </Typography>
        <Typography variant="body1" paragraph>
          RoomRoster is a business-to-business service intended for use by
          property management organizations and their staff. You must be at
          least 18 years old and authorized by your organization to use this
          platform. You are responsible for maintaining the confidentiality of
          your login credentials and for all activity that occurs under your
          account. Notify us immediately at{" "}
          <Link href="mailto:support@gleasondevelopment.com">
            support@gleasondevelopment.com
          </Link>{" "}
          if you suspect unauthorized use of your account.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          2. Permitted Use
        </Typography>
        <Typography variant="body1" paragraph>
          You may use RoomRoster solely for lawful internal property management
          purposes. You agree not to:
        </Typography>
        <Typography variant="body1" component="div">
          <ul>
            <li>Use the platform for any unlawful purpose or in violation of any applicable law</li>
            <li>Attempt to gain unauthorized access to any part of the service or another user's account</li>
            <li>Reverse engineer, decompile, or attempt to extract the source code of the platform</li>
            <li>Upload or transmit any malicious software, viruses, or harmful code</li>
            <li>Use the platform to harass, abuse, or harm any individual</li>
            <li>Resell or sublicense access to the platform without written consent</li>
            <li>Scrape, crawl, or systematically extract data from the service</li>
          </ul>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          3. Your Data
        </Typography>
        <Typography variant="body1" paragraph>
          You retain ownership of all data your organization enters into
          RoomRoster ("Customer Data"). By using the service, you grant Gleason
          Development LLC a limited, non-exclusive license to store, process,
          and display your Customer Data solely to operate and improve the
          platform. We do not claim ownership of your data and will not sell it
          to third parties.
        </Typography>
        <Typography variant="body1" paragraph>
          You are responsible for the accuracy and legality of any data you
          submit, including personal information about your employees. You
          represent that you have the right to share any data you input into the
          platform.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          4. Intellectual Property
        </Typography>
        <Typography variant="body1" paragraph>
          RoomRoster, including its software, design, user interface, and
          content created by Gleason Development LLC, is our exclusive property
          and is protected by applicable intellectual property laws. These Terms
          do not grant you any rights to our trademarks, logos, or other
          proprietary materials.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          5. Service Availability
        </Typography>
        <Typography variant="body1" paragraph>
          We strive to keep RoomRoster available at all times but do not
          guarantee uninterrupted access. We may perform scheduled or emergency
          maintenance, update the platform, or temporarily suspend access to
          address security or operational concerns. We will make reasonable
          efforts to minimize downtime and provide advance notice when possible.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          6. Disclaimer of Warranties
        </Typography>
        <Typography variant="body1" paragraph>
          RoomRoster is provided "as is" and "as available" without warranties
          of any kind, express or implied, including but not limited to
          warranties of merchantability, fitness for a particular purpose, or
          non-infringement. We do not warrant that the service will be error-free,
          secure, or continuously available.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          7. Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
          To the maximum extent permitted by applicable law, Gleason Development
          LLC shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages, including loss of data, revenue,
          or profits, arising out of or in connection with your use of
          RoomRoster, even if we have been advised of the possibility of such
          damages. Our total liability for any claim related to the service
          shall not exceed the amount you paid us in the twelve months preceding
          the claim.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          8. Indemnification
        </Typography>
        <Typography variant="body1" paragraph>
          You agree to indemnify, defend, and hold harmless Gleason Development
          LLC and its officers, employees, and agents from any claims, damages,
          losses, or expenses (including reasonable attorneys' fees) arising
          from your use of RoomRoster, your violation of these Terms, or your
          violation of any third-party rights.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          9. Termination
        </Typography>
        <Typography variant="body1" paragraph>
          We reserve the right to suspend or terminate your access to RoomRoster
          at any time, with or without notice, if we believe you have violated
          these Terms or engaged in conduct that may harm the service, other
          users, or Gleason Development LLC. Upon termination, your right to
          use the service ceases immediately. You may request an export of your
          Customer Data prior to termination by contacting us.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          10. Governing Law
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms are governed by the laws of the United States. Any
          disputes arising from these Terms or your use of RoomRoster shall be
          resolved through good-faith negotiation. If negotiation fails, disputes
          shall be submitted to binding arbitration or a court of competent
          jurisdiction in the United States.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          11. Changes to These Terms
        </Typography>
        <Typography variant="body1" paragraph>
          We may modify these Terms at any time. We will update the effective
          date at the top of this page when changes are made. Continued use of
          RoomRoster after changes are posted constitutes your acceptance of the
          updated Terms. We encourage you to review these Terms periodically.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
          12. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions about these Terms, please contact us at:
        </Typography>
        <Typography variant="body1">
          <strong>Gleason Development LLC</strong>
          <br />
          Email:{" "}
          <Link href="mailto:support@gleasondevelopment.com">
            support@gleasondevelopment.com
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default TermsAndConditions;
